import { amlbotAPI, type WidgetInitRequest } from "@/api";
import { create } from "zustand";
import { useConfigStore } from "./useConfigStore";
import { useAmlCheckStore, useAmlHistoryStore } from "./";
import { sendMessageToParent } from "@/utils/messaging";
import { MODE_FLOAT, MODE_INLINE, THEME_LIGHT, THEME_DARK } from "@/config";

interface User {
  balance: number;
  refCode?: string;
}

export interface UserStore {
  user: User;
  loading: boolean;

  // Actions
  setUser: (user: User) => void;
  setBalance: (balance: number) => void;
  fetchUserData: () => Promise<null>;
  setLoading: (loading: boolean) => void;
  clearUser: () => void;
  restoreIntentData: (token: string) => Promise<null>;
}

export const useUserStore = create<UserStore>((set, get, store) => ({
  user: {
    balance: 0,
    refCode: "",
  },
  loading: false,

  setLoading: (loading: boolean) => set({ loading }),
  setUser: (user: User) => set({ user }),
  setBalance: (balance: number) => set({ user: { ...get().user, balance } }),
  clearUser: () => set(store.getInitialState()),

  fetchUserData: async () => {
    const { loading } = get();

    if (loading) {
      return null;
    }

    try {
      set({ loading: true });

      const { appId, externalId, username, parentOrigin } =
        useConfigStore.getState();

      if (!!appId && !!externalId) {
        const payload: WidgetInitRequest = {
          appId,
          parentOrigin: parentOrigin ?? "",
          identifier: externalId,
          username: username ?? "",
        };

        const response = await amlbotAPI.widgetInit(payload);

        if (response.success) {
          set({
            user: {
              balance: response.data?.user?.balance,
              refCode: response.data?.user?.refCode || "",
            },
          });

          sendMessageToParent("SHOW_TOGGLER", {}, parentOrigin || "*");
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      set({ loading: false });
    }
    return null;
  },

  restoreIntentData: async (token: string) => {
    const { loading } = get();

    if (loading) {
      return null;
    }

    try {
      set({ loading: true });

      const { setTotalCount } = useAmlHistoryStore.getState();
      const { setInitParams, setExternalId, setUsername, setWidgetConfig } =
        useConfigStore.getState();
      const { setToken, setExternalAmlData } = useAmlCheckStore.getState();

      if (token) {
        const response = await amlbotAPI.getIntentData({ token });
        if (response.success) {
          const data = response.data;
          set({
            user: {
              balance: data?.user?.balance,
              refCode: data?.user?.refCode || "",
            },
          });
          setToken(data?.token);
          setTotalCount(data?.user?.totalCount || 0);
          setExternalAmlData(data?.amlData);
          setInitParams({
            parentOrigin: data?.amlData?.parentOrigin,
            appId: data?.amlData?.appId,
          });
          setExternalId(data?.amlData?.identifier);
          setWidgetConfig({
            theme:
              (data?.amlData?.theme as
                | typeof THEME_LIGHT
                | typeof THEME_DARK
                | undefined) || THEME_LIGHT,
            widgetMode:
              (data?.amlData?.mode as
                | typeof MODE_FLOAT
                | typeof MODE_INLINE
                | undefined) || MODE_FLOAT,
          });
          setUsername(data?.amlData?.username);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      set({ loading: false });
    }

    return null;
  },
}));
